import React, { useState } from "react";
import { format, parseISO } from "date-fns";
import { JourneyEvent } from "../../../api/analyticsAdmin";
import {
  SessionSummary,
  formatDurationLong,
} from "../../../helpers/analyticsPlusJourney";
import { GroupedTimeline } from "./GroupedTimeline";
import { RawEventList } from "./RawEventList";

type ViewMode = "grouped" | "raw";

const Chip: React.FC<{ color: string; children: React.ReactNode }> = ({
  color,
  children,
}) => (
  <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
    {children}
  </span>
);

export const SessionDetail: React.FC<{
  summary: SessionSummary;
  events: JourneyEvent[];
  businessNames: Map<string, string>;
}> = ({ summary, events, businessNames }) => {
  const [mode, setMode] = useState<ViewMode>("grouped");

  return (
    <div className="flex flex-col h-full">
      <div className="pb-4 border-b border-gray-200">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="text-lg font-medium text-gray-800">
            {format(parseISO(summary.start), "MMM d, yyyy · HH:mm")}
          </h3>
          <span className="text-sm text-gray-500">
            {formatDurationLong(summary.durationSeconds)} · {summary.eventCount} events
            {summary.bounce && " · bounce"}
          </span>
        </div>

        {summary.queries.length > 0 && (
          <div className="text-sm text-gray-600 mt-2">
            Searched:{" "}
            {summary.queries
              .map((q) => `"${q.query}"${q.totalCount !== null ? ` (${q.totalCount})` : ""}`)
              .join(", ")}
          </div>
        )}
        {(summary.businessesOpened > 0 || summary.outboundClicks > 0) && (
          <div className="text-sm text-gray-600 mt-1">
            {summary.businessesOpened > 0 && `Opened ${summary.businessesOpened} business`}
            {summary.businessesOpened > 0 && summary.outboundClicks > 0 && " · "}
            {summary.outboundClicks > 0 && `${summary.outboundClicks} outbound`}
          </div>
        )}
        {summary.platform && (
          <div className="text-xs text-gray-400 mt-1">Platform: {summary.platform}</div>
        )}

        {summary.typeCounts.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {summary.typeCounts.map((tc) => (
              <Chip key={tc.category} color={tc.color}>
                {tc.label} ×{tc.count}
              </Chip>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-1 py-3">
        {(["grouped", "raw"] as ViewMode[]).map((m) => (
          <button
            key={m}
            onClick={() => setMode(m)}
            className={`px-3 py-1 text-sm rounded border ${
              mode === m
                ? "border-primary bg-primary/10 text-primary"
                : "border-gray-300 text-gray-600 hover:bg-gray-50"
            }`}
          >
            {m === "grouped" ? "Grouped" : "Raw"}
          </button>
        ))}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto pr-1">
        {mode === "grouped" ? (
          <GroupedTimeline events={events} businessNames={businessNames} />
        ) : (
          <RawEventList events={events} />
        )}
      </div>
    </div>
  );
};
